import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Modal from "../../common/Modal";
import PrimaryButton from "../../common/PrimaryButton";
import styles from "./styles.module.scss";
import { IOrgClientCreated } from "../../../setup/api/services/organization/models";

interface Props {
  open: boolean;
  credentials: IOrgClientCreated | null;
  handleClose: () => void;
}

/**
 * Shown once, right after a Keycloak client is created (self-service creation in waffy-portal
 * shows the same thing) — clientSecret is never retrievable again after this, so the admin has
 * to copy it now. Shared between the org-approval flow's optional "also create a client" step
 * and the API clients settings page's own "create client" button.
 */
const ClientCredentialsModal = ({ open, credentials, handleClose }: Props) => {
  const { t } = useTranslation();

  const copyValue = async (value: string) => {
    try {
      await navigator?.clipboard?.writeText(value);
      toast.success(t("COPIED"));
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal
      visible={open}
      onCancel={handleClose}
      customModalClassName={styles.modal}
      customModalContainerClassName={styles.modalContainer}
    >
      <div className={styles.container}>
        <h1 className={styles.title}>{t("lbl_client_created_title")}</h1>
        <p className={styles.warning}>{t("lbl_client_created_warning")}</p>

        <div className={styles.field}>
          <label>{t("lbl_client_id")}</label>
          <div className={styles.valueRow}>
            <span className={styles.value}>{credentials?.clientId}</span>
            <button
              type="button"
              className={styles.copyBtn}
              onClick={() =>
                credentials?.clientId && copyValue(credentials.clientId)
              }
            >
              {t("lbl_copy")}
            </button>
          </div>
        </div>

        <div className={styles.field}>
          <label>{t("lbl_client_secret")}</label>
          <div className={styles.valueRow}>
            <span className={styles.value}>{credentials?.clientSecret}</span>
            <button
              type="button"
              className={styles.copyBtn}
              onClick={() =>
                credentials?.clientSecret && copyValue(credentials.clientSecret)
              }
            >
              {t("lbl_copy")}
            </button>
          </div>
        </div>

        <div className={styles.actions}>
          <PrimaryButton onClick={handleClose}>
            {t("lbl_client_created_done")}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default ClientCredentialsModal;
